/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { COLORS } from "@/data/product";
import WhatsAppOrderDialog from "@/components/WhatsAppOrderDialog";
import { ShoppingBag, Users, Euro, Package, LogOut, RefreshCw, Loader2, CheckCircle2, Clock, XCircle, Briefcase, Phone, Mail, ExternalLink, RotateCw } from "lucide-react";
import { toast } from "sonner";

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <div className="rounded-3xl bg-white border border-[#EADBD1] p-5">
    <div className="flex items-center justify-between">
      <span className="h-9 w-9 rounded-full bg-[#F5EFE6] grid place-items-center">
        <Icon className="h-4 w-4 text-[#C58C85]" />
      </span>
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D54]">{hint}</span>
    </div>
    <p className="font-serif text-3xl text-[#2C2420] mt-4">{value}</p>
    <p className="text-xs text-[#6B5D54] mt-1">{label}</p>
  </div>
);

const StatusBadge = ({ paymentStatus, status }) => {
  if (paymentStatus === "paid") return <Badge className="bg-[#25D366]/15 text-[#1EAB53] border-0"><CheckCircle2 className="h-3 w-3 mr-1" /> Payé</Badge>;
  if (status === "expired") return <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="h-3 w-3 mr-1" /> Expirée</Badge>;
  return <Badge className="bg-[#F5EFE6] text-[#6B5D54] border-0"><Clock className="h-3 w-3 mr-1" /> En cours</Badge>;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("fcv_admin_token");
  const [admin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fcv_admin") || "null"); } catch { return null; }
  });
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [shopify, setShopify] = useState(null);
  const [stock, setStock] = useState({}); // colorId -> in_stock
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState({}); // session_id -> bool

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, o, sub, st, p, sh] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/subscribers"),
        api.get("/admin/stock"),
        api.get("/admin/partners"),
        api.get("/admin/shopify/status"),
      ]);
      setStats(s.data);
      setOrders(o.data.orders || []);
      setSubscribers(sub.data.subscribers || []);
      setPartners(p.data.applications || []);
      setShopify(sh.data);
      const map = {};
      (st.data.stock || []).forEach((r) => { map[r.colorId] = r.in_stock; });
      setStock(map);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("fcv_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    if (hasToken) fetchAll();
  }, [hasToken]);
  /* eslint-enable */

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("fcv_admin_token");
    localStorage.removeItem("fcv_admin");
    navigate("/admin/login");
  };

  const toggleStock = async (colorId) => {
    const next = !(stock[colorId] !== false); // default true → toggle to false
    setStock((s) => ({ ...s, [colorId]: next }));
    try {
      await api.post(`/admin/stock/${colorId}`, { in_stock: next });
      toast.success(next ? "Couleur en stock" : "Couleur en rupture");
    } catch {
      toast.error("Échec — restauration");
      setStock((s) => ({ ...s, [colorId]: !next }));
    }
  };

  const resync = async (sessionId) => {
    setResyncing((r) => ({ ...r, [sessionId]: true }));
    try {
      const { data } = await api.post(`/admin/shopify/resync/${sessionId}`);
      if (data.shopify_order_name) {
        toast.success(`Commande synchronisée : ${data.shopify_order_name}`);
        await fetchAll();
      } else {
        toast.error("Échec de synchronisation");
      }
    } catch {
      toast.error("Échec de synchronisation");
    } finally {
      setResyncing((r) => ({ ...r, [sessionId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF9]">
      <header className="bg-white border-b border-[#EADBD1]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl tracking-tight">French Curl Vibes</span>
            <span className="text-xs uppercase tracking-[0.22em] text-[#C58C85]">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchAll} variant="outline" size="sm" className="rounded-full border-[#EADBD1]" data-testid="admin-refresh">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </Button>
            <Button onClick={logout} variant="ghost" size="sm" className="rounded-full" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="mb-2 text-sm text-[#6B5D54]">Bonjour {admin?.name || "Admin"}, voici votre tableau de bord.</div>

        {shopify?.configured && shopify?.shop && (
          <div className="mt-4 rounded-2xl bg-white border border-[#EADBD1] px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="shopify-banner">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-[#95BF47]/15 grid place-items-center">
                <ShoppingBag className="h-4 w-4 text-[#5E8E3E]" />
              </span>
              <div>
                <p className="text-sm font-medium text-[#2C2420]">Boutique Shopify connectée : {shopify.shop.name}</p>
                <p className="text-xs text-[#6B5D54]">{shopify.shop.domain} · {shopify.shop.currency} · {shopify.shop.country}</p>
              </div>
            </div>
            <a
              href={`https://${shopify.shop.domain}/admin`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#5E8E3E] hover:underline inline-flex items-center gap-1"
              data-testid="shopify-admin-link"
            >
              Ouvrir Shopify Admin <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <StatCard icon={ShoppingBag} label="Commandes totales" value={stats?.orders_total ?? "—"} hint="Toutes" />
          <StatCard icon={CheckCircle2} label="Commandes payées" value={stats?.orders_paid ?? "—"} hint="Stripe" />
          <StatCard icon={Euro} label="Chiffre d'affaires" value={formatEUR(stats?.revenue_eur)} hint="Cumul" />
          <StatCard icon={Users} label="Abonnés newsletter" value={stats?.subscribers ?? "—"} hint="Total" />
          <StatCard icon={Briefcase} label="Candidatures partenaires" value={stats?.partners ?? "—"} hint="Total" />
        </div>

        <Tabs defaultValue="orders" className="mt-10">
          <TabsList className="bg-white border border-[#EADBD1] rounded-full p-1 h-auto">
            <TabsTrigger value="orders" className="rounded-full px-5 data-[state=active]:bg-[#2C2420] data-[state=active]:text-white" data-testid="tab-orders">Commandes</TabsTrigger>
            <TabsTrigger value="subscribers" className="rounded-full px-5 data-[state=active]:bg-[#2C2420] data-[state=active]:text-white" data-testid="tab-subscribers">Abonnés</TabsTrigger>
            <TabsTrigger value="partners" className="rounded-full px-5 data-[state=active]:bg-[#2C2420] data-[state=active]:text-white" data-testid="tab-partners">Partenaires</TabsTrigger>
            <TabsTrigger value="stock" className="rounded-full px-5 data-[state=active]:bg-[#2C2420] data-[state=active]:text-white" data-testid="tab-stock">Stock couleurs</TabsTrigger>
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <div className="mb-4 flex justify-end">
              <WhatsAppOrderDialog onCreated={fetchAll} />
            </div>
            <div className="rounded-3xl bg-white border border-[#EADBD1] overflow-hidden">
              {loading && orders.length === 0 ? (
                <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#6B5D54]" /></div>
              ) : orders.length === 0 ? (
                <div className="p-12 text-center text-[#6B5D54] text-sm">Aucune commande pour le moment.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5EFE6] text-[#6B5D54] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-5 py-3">Date</th>
                        <th className="text-left px-5 py-3">Source</th>
                        <th className="text-left px-5 py-3">Email</th>
                        <th className="text-left px-5 py-3">Articles</th>
                        <th className="text-left px-5 py-3">Livraison</th>
                        <th className="text-right px-5 py-3">Montant</th>
                        <th className="text-left px-5 py-3">Statut</th>
                        <th className="text-left px-5 py-3">Shopify</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#EADBD1]">
                      {orders.map((o) => (
                        <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-[#F5EFE6]/50">
                          <td className="px-5 py-3 whitespace-nowrap text-[#2C2420]">{fmtDate(o.created_at)}</td>
                          <td className="px-5 py-3">
                            {o.source === "whatsapp" ? (
                              <Badge className="bg-[#25D366]/15 text-[#1EAB53] border-0 text-[10px]">WhatsApp</Badge>
                            ) : o.source === "whatsapp-link" ? (
                              <Badge className="bg-[#C58C85]/15 text-[#A6726B] border-0 text-[10px]">WhatsApp link</Badge>
                            ) : (
                              <Badge className="bg-[#635BFF]/15 text-[#635BFF] border-0 text-[10px]">Stripe</Badge>
                            )}
                          </td>
                          <td className="px-5 py-3 text-[#2C2420]">{o.email || "—"}</td>
                          <td className="px-5 py-3 text-[#2C2420] max-w-xs truncate">{o.metadata?.cart || "—"}</td>
                          <td className="px-5 py-3 text-[#2C2420]">{o.shipping_label}</td>
                          <td className="px-5 py-3 text-right font-medium text-[#2C2420]">{formatEUR(o.amount)}</td>
                          <td className="px-5 py-3"><StatusBadge paymentStatus={o.payment_status} status={o.status} /></td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            {o.shopify_order_name ? (
                              <a
                                href={o.shopify_order_admin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[#5E8E3E] hover:underline text-xs font-medium"
                                data-testid={`shopify-link-${o.id}`}
                              >
                                {o.shopify_order_name} <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : o.payment_status === "paid" ? (
                              <button
                                onClick={() => resync(o.session_id)}
                                disabled={resyncing[o.session_id]}
                                className="inline-flex items-center gap-1 text-xs text-[#6B5D54] hover:text-[#2C2420]"
                                data-testid={`resync-btn-${o.id}`}
                              >
                                {resyncing[o.session_id] ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <><RotateCw className="h-3 w-3" /> Sync</>
                                )}
                              </button>
                            ) : (
                              <span className="text-xs text-[#6B5D54]/50">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* SUBSCRIBERS */}
          <TabsContent value="subscribers" className="mt-6">
            <div className="rounded-3xl bg-white border border-[#EADBD1] overflow-hidden">
              {subscribers.length === 0 ? (
                <div className="p-12 text-center text-[#6B5D54] text-sm">Aucun abonné pour le moment.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F5EFE6] text-[#6B5D54] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Date</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EADBD1]">
                    {subscribers.map((s) => (
                      <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="hover:bg-[#F5EFE6]/50">
                        <td className="px-5 py-3 whitespace-nowrap text-[#2C2420]">{fmtDate(s.subscribed_at)}</td>
                        <td className="px-5 py-3 text-[#2C2420]">{s.email}</td>
                        <td className="px-5 py-3 text-[#6B5D54]">{s.source || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <Separator className="my-6 bg-[#EADBD1]" />
            <p className="text-xs text-[#6B5D54]">
              💡 Pour envoyer une campagne, exportez les emails et utilisez Resend / Brevo. L&apos;intégration Resend automatique pourra être activée en ajoutant <code className="bg-[#F5EFE6] px-1 py-0.5 rounded">RESEND_API_KEY</code> dans le backend.
            </p>
          </TabsContent>

          {/* PARTNERS */}
          <TabsContent value="partners" className="mt-6">
            <div className="rounded-3xl bg-white border border-[#EADBD1] overflow-hidden">
              {partners.length === 0 ? (
                <div className="p-12 text-center text-[#6B5D54] text-sm">Aucune candidature pour le moment.</div>
              ) : (
                <ul className="divide-y divide-[#EADBD1]">
                  {partners.map((p) => (
                    <li key={p.id} className="p-5 hover:bg-[#F5EFE6]/50" data-testid={`partner-row-${p.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-[#2C2420]">{p.full_name}{p.salon_name ? ` · ${p.salon_name}` : ""}</p>
                          <p className="text-xs text-[#6B5D54] mt-1">{p.city} · {fmtDate(p.created_at)}</p>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-[#2C2420]">
                          <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[#C58C85]"><Mail className="h-3 w-3" /> {p.email}</a>
                          <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[#C58C85]"><Phone className="h-3 w-3" /> {p.phone}</a>
                        </div>
                      </div>
                      {(p.socials || p.monthly_clients) && (
                        <div className="mt-2 text-xs text-[#6B5D54] space-x-3">
                          {p.socials && <span>📱 {p.socials}</span>}
                          {p.monthly_clients && <span>👥 {p.monthly_clients} clientes/mois</span>}
                        </div>
                      )}
                      {p.message && <p className="mt-3 text-sm text-[#2C2420]/85 italic">&ldquo;{p.message}&rdquo;</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* STOCK */}
          <TabsContent value="stock" className="mt-6">
            <div className="rounded-3xl bg-white border border-[#EADBD1] p-6">
              <p className="text-sm text-[#6B5D54] mb-4">
                Cliquez sur une couleur pour la basculer en &quot;rupture&quot;. Par défaut toutes les couleurs sont en stock.
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                {COLORS.map((c) => {
                  const inStock = stock[c.id] !== false; // default true
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleStock(c.id)}
                      title={`${c.name} (${c.code}) — ${inStock ? "en stock" : "rupture"}`}
                      data-testid={`admin-stock-${c.id}`}
                      className={`group relative aspect-square rounded-xl border transition-all ${
                        inStock ? "border-[#EADBD1] hover:border-[#C58C85]" : "border-red-300 opacity-40"
                      }`}
                      style={{ background: c.swatch }}
                    >
                      {!inStock && (
                        <span className="absolute inset-0 grid place-items-center bg-black/30 rounded-xl">
                          <XCircle className="h-5 w-5 text-white" />
                        </span>
                      )}
                      <span className="absolute -bottom-5 inset-x-0 text-[9px] text-[#6B5D54] text-center font-mono opacity-0 group-hover:opacity-100">
                        {c.code}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
